import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import {
  TrendingUp, Building2, Clock, Filter, Sparkles, Inbox, ArrowRight,
} from "lucide-react";
import { SectionCard, DashboardHeader } from "../components/dashboard/shells";

const STAGE_COLOR = {
  pending:   "#94A3B8",
  screening: "#0EA5E9",
  interview: "#F59E0B",
  offer:     "#22C55E",
  hired:     "#10B981",
  onboarded: "#006CFF",
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(iso).slice(0, 10); }
};

function Funnel({ stages }) {
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="space-y-2.5" data-testid="institute-funnel">
      {stages.map((s) => {
        const w = Math.max(6, (s.count / max) * 100);
        const color = STAGE_COLOR[s.key] || "#64748B";
        return (
          <div key={s.key} className="flex items-center gap-3">
            <div className="text-[10px] font-bold w-24 text-slate-700 uppercase tracking-wider shrink-0">{s.label}</div>
            <div className="flex-1 h-8 rounded-md bg-slate-50 overflow-hidden">
              <div
                className="h-full rounded-md flex items-center px-3 text-white text-sm font-bold transition-all tabular-nums"
                style={{ width: `${w}%`, background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)` }}
                data-testid={`funnel-${s.key}`}
              >
                {s.count}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RecruiterHeatmap({ rows }) {
  if (!rows.length) return <div className="text-sm text-muted">No recruiter activity yet.</div>;
  const STAGES = ["pending", "screening", "interview", "offer", "hired", "onboarded"];
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="dense w-full" data-testid="recruiter-heatmap-table">
        <thead>
          <tr>
            <th className="text-left">Company</th>
            {STAGES.map((s) => (
              <th key={s} className="text-center text-[10px] uppercase tracking-wider">{s}</th>
            ))}
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.company} data-testid={`heat-row-${r.company}`} className="hover:bg-slate-50/70 transition">
              <td className="font-semibold">
                <span className="inline-flex items-center gap-2">
                  <Building2 size={13} className="text-signal" /> {r.company}
                </span>
              </td>
              {STAGES.map((s) => {
                const v = r[s] || 0;
                const intensity = Math.min(0.9, 0.18 + v * 0.08);
                return (
                  <td key={s} className="text-center">
                    {v > 0 ? (
                      <span
                        className="inline-block min-w-[28px] px-2 py-0.5 rounded text-white text-xs font-bold tabular-nums"
                        style={{ background: STAGE_COLOR[s], opacity: intensity }}
                      >{v}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                );
              })}
              <td className="text-right font-bold tabular-nums">{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityFeed({ items }) {
  if (!items.length) {
    return (
      <div className="text-sm text-muted flex flex-col items-center gap-2 py-8">
        <Inbox size={20} className="opacity-40" />
        No status changes yet.
      </div>
    );
  }
  return (
    <ol className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1" data-testid="activity-feed">
      {items.map((it, idx) => (
        <li
          key={`${it.student_name}-${it.ts}-${idx}`}
          className="flex items-start gap-2.5 pb-2.5 border-b border-rule last:border-b-0"
        >
          <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: STAGE_COLOR[it.status] || "#64748b" }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm leading-snug">
              <b className="text-slate-900">{it.student_name}</b>{" "}
              <span className="text-muted">→</span>{" "}
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white"
                style={{ background: STAGE_COLOR[it.status] }}
              >
                {it.status}
              </span>{" "}
              <span className="text-muted">at</span>{" "}
              <span className="font-semibold">{it.company}</span>
              {it.title ? <span className="text-muted"> · {it.title}</span> : null}
            </div>
            <div className="text-xs text-muted mt-0.5 flex items-center gap-1"><Clock size={11} /> {fmtDate(it.ts)}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function InstitutePipeline() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/institute/pipeline").then((r) => setData(r.data)).catch((e) => setErr(formatError(e)));
  }, []);

  if (err) return <div className="bg-white border border-rule rounded-xl p-5 text-red-600" data-testid="pipeline-error">{err}</div>;
  if (!data) return <div className="text-muted p-4" data-testid="pipeline-loading">Loading live pipeline…</div>;

  const total = data.applications_total || 0;
  return (
    <div className="space-y-4" data-testid="institute-pipeline-page">
      <DashboardHeader
        eyebrow="Live Placement Pipeline"
        title="Where your students stand — right now"
        subtitle={`${total.toLocaleString()} application${total === 1 ? "" : "s"} across all postings · auto-refreshes on page load.`}
        meta={
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-rule text-sm font-semibold hover:border-signal transition"
            onClick={() => window.location.reload()}
            data-testid="pipeline-refresh"
          >
            <Sparkles size={13} /> Refresh
          </button>
        }
      />

      <div className="grid lg:grid-cols-5 gap-4">
        <SectionCard
          eyebrow="Funnel"
          title="Aggregate, all postings"
          action={<span className="text-[10px] uppercase tracking-wider text-muted inline-flex items-center gap-1"><Filter size={11} /> live</span>}
          className="lg:col-span-3"
          testid="funnel-card"
        >
          <Funnel stages={data.funnel || []} />
        </SectionCard>

        <SectionCard
          eyebrow="Recent activity"
          title="Latest status changes"
          action={<ArrowRight size={13} className="text-muted" />}
          className="lg:col-span-2"
          testid="activity-card"
        >
          <ActivityFeed items={data.activity || []} />
        </SectionCard>
      </div>

      <SectionCard
        eyebrow="Recruiter × Stage heatmap"
        title="Top recruiters by placement activity"
        action={<span className="text-xs text-muted">Top 8 by total</span>}
        testid="heatmap-card"
      >
        <RecruiterHeatmap rows={data.recruiter_heatmap || []} />
      </SectionCard>
    </div>
  );
}
